/* Taboola Carousel Web Component
 * Drop-in carousel with horizontal scroll-snap + dot navigation.
 * Works without JS too — JS only adds active dot tracking and dot click handling.
 *
 * Usage in Liquid:
 *   <taboola-carousel data-track-class="section-taboola-stories__list">
 *     <div class="section-taboola-stories__list">
 *       <article class="section-taboola-stories__card">…</article>
 *       <article class="section-taboola-stories__card">…</article>
 *     </div>
 *     <div class="taboola-carousel-dots taboola-carousel-dots--hide-on-desktop" data-dots>
 *       <button type="button" class="taboola-carousel-dots__dot" aria-current="true" aria-label="Go to slide 1"></button>
 *       <button type="button" class="taboola-carousel-dots__dot" aria-label="Go to slide 2"></button>
 *     </div>
 *   </taboola-carousel>
 */
(function () {
  if (window.customElements && window.customElements.get('taboola-carousel')) return;

  class TaboolaCarousel extends HTMLElement {
    constructor() {
      super();
      this._onScroll = this._onScroll.bind(this);
      this._onDotClick = this._onDotClick.bind(this);
      this._scrollTimeout = null;
    }

    connectedCallback() {
      const trackSelector = this.dataset.trackClass
        ? '.' + this.dataset.trackClass
        : '[data-carousel-track]';
      this.track = this.querySelector(trackSelector);
      this.dotsContainer = this.querySelector('[data-dots]');

      if (!this.track) {
        // Fallback: first child is track
        this.track = this.firstElementChild;
      }
      if (!this.track) return;

      this.cards = Array.from(this.track.children);
      this.dots = this.dotsContainer
        ? Array.from(this.dotsContainer.querySelectorAll('.taboola-carousel-dots__dot'))
        : [];

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this._onDotClick(i));
      });

      this._update();
    }

    disconnectedCallback() {
      if (this.track) {
        this.track.removeEventListener('scroll', this._onScroll);
      }
    }

    _onScroll() {
      if (this._scrollTimeout) clearTimeout(this._scrollTimeout);
      this._scrollTimeout = setTimeout(() => this._update(), 100);
    }

    _onDotClick(index) {
      const card = this.cards[index];
      if (!card) return;
      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const offset = cardRect.left - trackRect.left + this.track.scrollLeft;
      this.track.scrollTo({ left: offset, behavior: 'smooth' });
    }

    _update() {
      if (!this.cards.length) return;
      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;

      let activeIndex = 0;
      let minDistance = Infinity;

      this.cards.forEach((card, i) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - trackCenter);
        if (distance < minDistance) {
          minDistance = distance;
          activeIndex = i;
        }
      });

      this.dots.forEach((dot, i) => {
        if (i === activeIndex) {
          dot.setAttribute('aria-current', 'true');
          dot.classList.add('taboola-carousel-dots__dot--active');
        } else {
          dot.removeAttribute('aria-current');
          dot.classList.remove('taboola-carousel-dots__dot--active');
        }
      });
    }
  }

  window.customElements.define('taboola-carousel', TaboolaCarousel);

  // Theme Editor compatibility — re-init carousels when sections reload
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      event.target.querySelectorAll('taboola-carousel').forEach(function (el) {
        if (el.connectedCallback) el.connectedCallback();
      });
    });
  }
})();
